import React, { useState, useEffect } from 'react'
import { generateAffiliateLink, getProviderInfo } from '../utils/affiliateLinks'
import AffiliateProviderChoice from './AffiliateProviderChoice'

interface Book {
  isbn?: string
  asin?: string
  bookshop_slug?: string
  title: string
  author: string
}

interface BuyBookButtonProps {
  book: Book
  userProvider?: string | null  // User's preferred provider (null if not set)
  variant?: 'primary' | 'secondary' | 'minimal'
  size?: 'sm' | 'md' | 'lg'
  className?: string
  onProviderSelected?: (provider: string) => void  // Callback when user selects provider
}

export default function BuyBookButton({
  book,
  userProvider,
  variant = 'secondary',
  size = 'md',
  className = '',
  onProviderSelected
}: BuyBookButtonProps) {
  const [showProviderChoice, setShowProviderChoice] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState<string | null>(userProvider || null)

  // Sync local state when userProvider prop changes (but don't override if we already have a selection)
  useEffect(() => {
    if (userProvider) {
      setSelectedProvider(userProvider)
    }
  }, [userProvider])

  // Determine which provider to use: local selection takes precedence, then prop
  const activeProvider = selectedProvider || userProvider

  // If user hasn't set a preference, show modal on click
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!activeProvider) {
      e.preventDefault()
      setShowProviderChoice(true)
    }
    // Otherwise, let the link work normally
  }

  const handleProviderChosen = (provider: string) => {
    setSelectedProvider(provider)
    setShowProviderChoice(false)

    if (onProviderSelected) {
      onProviderSelected(provider)
    }

    // Generate the affiliate link and open it immediately
    const linkResult = generateAffiliateLink(book, provider)
    if (linkResult) {
      window.open(linkResult.url, '_blank', 'noopener,noreferrer')
    }
  }

  const linkResult = generateAffiliateLink(book, activeProvider || undefined)

  if (!linkResult) {
    // If we can't generate a link, don't render the button
    return null
  }

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  }

  // Use a consistent light orange color for all providers
  const brandColor = '#FF9900'

  const variantClasses = {
    primary: 'bg-[#FF9900] hover:bg-[#E88B00] text-white font-semibold shadow-sm',
    secondary: 'bg-white border-2 border-[#FF9900] text-[#FF9900] hover:bg-orange-50 font-semibold',
    minimal: 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
  }

  // Generate disclosure text based on provider
  const getDisclosure = () => {
    if (linkResult.provider === 'amazon') {
      return 'As an Amazon Associate, we earn from qualifying purchases.'
    } else if (linkResult.provider === 'bookshop') {
      return 'We may earn a commission from purchases made through this link.'
    }
    return 'Affiliate link - we may earn a commission from purchases.'
  }

  return (
    <>
      <a
        href={linkResult.url}
        target="_blank"
        rel="noopener noreferrer"
        title={getDisclosure()}
        onClick={handleClick}
        className={`
          inline-flex items-center justify-center gap-2 rounded-lg transition-all
          ${sizeClasses[size]}
          ${variantClasses[variant]}
          ${className}
        `}
      >
        {/* Book icon - generic across all providers */}
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
        <span>Buy</span>
      </a>

      {showProviderChoice && (
        <AffiliateProviderChoice onClose={handleProviderChosen} />
      )}
    </>
  )
}
